import { ReactElement, ReactNode } from 'react';

import { MultiSelectOption } from 'components/AsyncSelect/types';
import { PillFilterOption } from 'components/AsyncSelect/useAsyncMultiSelectProps';

import { SearchRequest, SearchResponse } from 'utils/search/types';

import { SearchFrameHashState } from 'types/hash-state';

import { GetDownloadDataFn } from '../DownloadButton';

export interface ListItemsSelection<HitT> {
  type: 'list';
  items: HitT[];
  count: number;
}

export interface FilterItemsSelection {
  type: 'filter';
  filters?: Obj<string[]>;
  /** This corresponds to the user-friendly title of the filter keys */
  filterTitleMapping?: Obj<string | undefined>;
  count: number;
}

export type SearchFrameSelection<HitT> = ListItemsSelection<HitT> | FilterItemsSelection;

export interface SearchFrameContext<HitT> {
  hashState: SearchFrameHashState;
  searchRequest: SearchRequest;
  selection?: SearchFrameSelection<HitT>;
  searchIndexResponse?: SearchResponse<HitT>;
  searchIndexReload: () => void;
  clearSelection: () => void;
  /** true if there is a row selection either via selectAll or selectedRows */
  canDownload: boolean;
  getDownloadData: GetDownloadDataFn;
}

export enum FacetRenderType {
  /** Facet rendered as a dropdown */
  Dropdown = 'dropdown',
  /** Facet rendered as a pill */
  Pill = 'pill',
}

export interface SearchFrameRowSelection<HitT> {
  selectedRows?: HitT[];
  selectedAll?: boolean;
}

export type FilterType = 'multiple' | 'single';

export interface NumericFacet<HitT> {
  title: string;
  field: keyof HitT extends string ? string : never;
  labelFormatFn?: (value: number) => string;
}

export interface ValueFacet<HitT> {
  title?: string;
  icon?: ReactElement;
  field: keyof HitT extends string ? string : never;
  hidden?: boolean;
  queryPlaceholder?: string;
  /** Use a string to choose a specific value (or string array for multiple) or true to use the first value */
  defaultValue?: string | string[] | boolean;
  filterType?: FilterType;
  renderType?: FacetRenderType;
  /** IMPORTANT NOTE: this will only frontend sort, so if there are more that 100 facet values, this will not work */
  sortBy?: 'alpha' | 'count' | 'none';
  labelFormatFn?: (value: string) => string;
  /** Static list of options for the facet */
  options?: MultiSelectOption[] | PillFilterOption[];
  triggerButton?: ReactNode;
}

export interface ExternalFacet {
  title: string;
  icon?: ReactElement;
  field: string;
  options: MultiSelectOption[];
  /** Use a string to choose a specific value or true to use the first value */
  defaultValue?: string;
  filterType?: FilterType;
}
